import { parsePhoneNumberFromString } from "libphonenumber-js";

import { DEFINITIONS, MASKS } from "@/constants/mask.constants";

const DEFAULT_COUNTRY = "BR";

/** Dígitos de DDI (55) + DDD (2) antes do assinante. */
export const BRAZIL_PHONE_DDI_DDD_DIGIT_COUNT = 4;

export function getBrazilPhoneSubscriberLength(digits: string): number {
  const numbers = digits.replace(/\D/g, "");
  return Math.max(0, numbers.length - BRAZIL_PHONE_DDI_DDD_DIGIT_COUNT);
}

export function shouldUseBrazilPhone9DigitMask(digits: string): boolean {
  return getBrazilPhoneSubscriberLength(digits) > 8;
}

type DynamicMaskedPhone = {
  unmaskedValue: string;
  compiledMasks: { mask: string }[];
};

/** Escolhe máscara 9999-9999 (8 dígitos) ou 99999-9999 (9 dígitos). */
export function dispatchBrazilPhoneMask(
  appended: string,
  dynamicMasked: DynamicMaskedPhone
): { mask: string } {
  const digits = `${dynamicMasked.unmaskedValue}${appended ?? ""}`.replace(/\D/g, "");
  const maskIndex = shouldUseBrazilPhone9DigitMask(digits) ? 0 : 1;
  return dynamicMasked.compiledMasks[maskIndex];
}

export const BRAZIL_PHONE_DYNAMIC_MASK = {
  mask: [
    { mask: MASKS.PHONE_9, definitions: DEFINITIONS.PHONE },
    { mask: MASKS.PHONE_8, definitions: DEFINITIONS.PHONE },
  ],
  dispatch: dispatchBrazilPhoneMask,
};

const normalizePhoneValue = (phoneNumberString: string): string => {
  const trimmedValue = phoneNumberString.trim();
  if (!trimmedValue) return "";

  if (trimmedValue.startsWith("+")) {
    return `+${trimmedValue.slice(1).replace(/\D/g, "")}`;
  }

  return trimmedValue.replace(/\D/g, "");
};

export function formatPhoneNumber(phoneNumberString: string): string {
  if (!phoneNumberString) return "";

  const normalizedValue = normalizePhoneValue(phoneNumberString);
  if (!normalizedValue) return "";

  const digits = normalizedValue.replace(/\D/g, "");
  const hasInternationalPrefix = normalizedValue.startsWith("+");

  // BR com DDI 55: 12 dígitos (8 no assinante) ou 13 dígitos (9 no assinante)
  if (digits.startsWith("55") && digits.length >= 12 && digits.length <= 13) {
    return formatDigitsToPhoneMaskInput(digits);
  }

  // BR sem DDI: DDD + assinante (10 ou 11 dígitos).
  // Só vale quando o valor não veio com + explícito, senão um número internacional
  // como +1 555 430-8169 seria lido como brasileiro (+55 (15) 55430-8169).
  if (
    !hasInternationalPrefix &&
    !digits.startsWith("55") &&
    digits.length >= 10 &&
    digits.length <= 11
  ) {
    return formatDigitsToPhoneMaskInput(`55${digits}`);
  }

  const valueToParse = hasInternationalPrefix ? normalizedValue : `+${normalizedValue}`;

  const parsedInternational = parsePhoneNumberFromString(valueToParse);
  if (parsedInternational) {
    const countryCallingCode = parsedInternational.countryCallingCode;
    const nationalFormatted = parsedInternational.formatNational();
    return `+${countryCallingCode} ${nationalFormatted}`;
  }

  if (!hasInternationalPrefix) {
    const parsedDefaultCountry = parsePhoneNumberFromString(normalizedValue, DEFAULT_COUNTRY);
    if (parsedDefaultCountry) {
      const countryCallingCode = parsedDefaultCountry.countryCallingCode;
      const nationalFormatted = parsedDefaultCountry.formatNational();
      return `+${countryCallingCode} ${nationalFormatted}`;
    }
  }

  return hasInternationalPrefix ? normalizedValue : `+${normalizedValue}`;
}

export function formatPhoneNumberBRSpaces(phoneNumberString: string): string {
  return formatPhoneNumber(phoneNumberString);
}

export function buildFullPhone(ddi: string, ddd: string, phone: string): string {
  return `${ddi}${ddd}${phone}`;
}

/**
 * Converte dígitos armazenados (ex.: 5511999999999) para exibição na máscara BR.
 * 9 dígitos no assinante → 99999-9999 | 8 dígitos → 9999-9999 (sempre 4 após o hífen).
 */
export function formatDigitsToPhoneMaskInput(digits: string): string {
  const numbers = digits.replace(/\D/g, "");
  if (!numbers) return "";

  const ddi = numbers.slice(0, 2);
  const ddd = numbers.slice(2, 4);
  const subscriber = numbers.slice(4);

  if (numbers.length <= 2) return `+${ddi}`;
  if (numbers.length <= 4) return `+${ddi} (${ddd}`;

  if (subscriber.length <= 8) {
    const first = subscriber.slice(0, 4);
    const second = subscriber.slice(4);
    return second ? `+${ddi} (${ddd}) ${first}-${second}` : `+${ddi} (${ddd}) ${first}`;
  }

  const first = subscriber.slice(0, 5);
  const second = subscriber.slice(5, 9);
  return second ? `+${ddi} (${ddd}) ${first}-${second}` : `+${ddi} (${ddd}) ${first}`;
}

export const handlePhoneChanges = (
  name: string,
  value: string,
  setFieldValue: (field: string, value: string, shouldValidate?: boolean | undefined) => void
) => {
  const formattedPhone = value.replace(/\D/g, "");
  setFieldValue(name, formattedPhone);
};

export function getPhoneDDI(phoneNumber: string) {
  const rawNumber = phoneNumber.replace(/\D/g, "");
  const ddd = rawNumber.substring(0, 2);
  return ddd;
}

export function getPhoneDDD(phoneNumber: string) {
  const rawNumber = phoneNumber.replace(/\D/g, "");
  const ddd = rawNumber.substring(2, 4);
  return ddd;
}

export function getPhoneWithoutDDD(phoneNumber: string) {
  const rawNumber = phoneNumber.replace(/\D/g, "");
  const number = rawNumber.substring(4);
  return number;
}

export function buildPhoneProperties(phoneNumber: string): {
  ddi: string;
  ddd: string;
  phone: string;
} {
  const ddi = getPhoneDDI(phoneNumber);
  const ddd = getPhoneDDD(phoneNumber);
  const phone = getPhoneWithoutDDD(phoneNumber);

  return { ddi, ddd, phone };
}

export function removeDDIFromPhoneNumber(phoneNumberString: string): string {
  const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{2})(\d{2})(\d{5})(\d{4})$/);

  if (match) {
    return match[2] + match[3] + match[4];
  }

  return phoneNumberString;
}
